<template>
  <div>
    <base-page-title :titleText="pageTitle" />
    <b-col class="col-12 p-3 mx-auto">
      <b-card no-body class="p-3 mb-2">
        <div class="d-flex flex-wrap align-items-center">
          <h6 class="font-weight-bold mb-0 mr-auto">
            {{ $t("page.releaseNotes.title") }}
          </h6>
          <b-button
            variant="outline-secondary"
            size="sm"
            class="mr-2 my-1"
            :disabled="loading"
            @click="loadNotes"
          >
            <span
              v-if="loading"
              class="spinner-border spinner-border-sm mr-1"
            />
            {{ $t("releaseNotes.refresh") }}
          </b-button>
          <b-button
            variant="outline-primary"
            size="sm"
            class="mr-2 my-1"
            @click="openJira"
          >
            {{ $t("releaseNotes.importJira") }}
          </b-button>
          <b-button size="sm" class="my-1" @click="openEditor(null)">
            {{ $t("releaseNotes.newNote") }}
          </b-button>
        </div>
        <hr />

        <div v-if="listError" class="alert alert-danger py-2 mb-3">
          {{ listError }}
        </div>
        <div v-if="actionError" class="alert alert-danger py-2 mb-3">
          {{ actionError }}
        </div>

        <b-table
          :items="notes"
          :fields="tableFields"
          :busy="loading"
          primary-key="_id"
          show-empty
          small
          hover
          responsive
          class="mb-0"
        >
          <template #table-busy>
            <div class="text-center text-muted my-3">
              <span class="spinner-border spinner-border-sm mr-2" />
            </div>
          </template>
          <template #empty>
            <div class="text-center text-muted my-3">
              {{ $t("releaseNotes.empty") }}
            </div>
          </template>
          <template #cell(version)="{ item }">
            {{ item.version || "—" }}
          </template>
          <template #cell(title)="{ item }">
            <span class="font-weight-bold">{{ noteTitle(item) || "—" }}</span>
            <span class="text-muted small ml-1">
              ({{ (item.sourceLocale || "").toUpperCase() }})
            </span>
          </template>
          <template #cell(tag)="{ item }">
            <div class="text-nowrap">
              <span class="rn-chip" :class="chipClass(item)">
                {{ $t(`releaseNotes.tag.${item.tag || "new"}`) }}
              </span>
              <span
                v-if="item.tag === 'issue'"
                class="rn-chip ml-1"
                :class="`rn-chip--${issueStatusOf(item)}`"
              >
                {{ $t(`releaseNotes.issueStatus.${issueStatusOf(item)}`) }}
              </span>
            </div>
            <div
              v-if="item.tag === 'issue' && item.resolvedAt"
              class="small text-muted text-nowrap"
            >
              {{
                $t("releaseNotes.help.resolvedAt", {
                  date: formatDate(item.resolvedAt),
                })
              }}
            </div>
            <div
              v-if="item.tag === 'upcoming' && item.expectedAt"
              class="small text-muted text-nowrap"
            >
              {{
                $t("releaseNotes.help.expectedList", {
                  date: formatDay(item.expectedAt),
                })
              }}
            </div>
          </template>
          <template #cell(importance)="{ item }">
            {{ $t(`releaseNotes.importance.${item.importance || "minor"}`) }}
          </template>
          <template #cell(audience)="{ item }">
            <span class="badge" :class="audienceBadge(item.audience)">
              {{ $t(`releaseNotes.audience.${item.audience || "all"}`) }}
            </span>
          </template>
          <template #cell(status)="{ item }">
            <span class="badge" :class="statusBadge(noteStatus(item))">
              {{ $t(`releaseNotes.status.${noteStatus(item)}`) }}
            </span>
            <div
              v-if="item.expiresAt && noteStatus(item) !== 'expired'"
              class="small text-muted text-nowrap"
            >
              {{
                $t("releaseNotes.help.expiresList", {
                  date: formatDate(item.expiresAt),
                })
              }}
            </div>
          </template>
          <template #cell(publishedAt)="{ item }">
            <span class="text-nowrap">{{ formatDate(item.publishedAt) }}</span>
          </template>
          <template #cell(views)="{ item }">
            {{ (item.stats && item.stats.views) || 0 }}
          </template>
          <template #cell(clicks)="{ item }">
            {{ (item.stats && item.stats.clicks) || 0 }}
          </template>
          <template #cell(actions)="{ item }">
            <div class="text-nowrap">
              <b-button
                size="sm"
                variant="outline-primary"
                class="mr-1"
                @click="openEditor(item)"
              >
                {{ $t("releaseNotes.actions.edit") }}
              </b-button>
              <b-button
                v-if="item.tag === 'issue' && issueStatusOf(item) === 'ongoing'"
                size="sm"
                variant="outline-success"
                class="mr-1"
                :disabled="busyId === item._id"
                @click="markResolved(item)"
              >
                <span
                  v-if="busyId === item._id && busyAction === 'resolve'"
                  class="spinner-border spinner-border-sm mr-1"
                />
                {{ $t("releaseNotes.actions.markResolved") }}
              </b-button>
              <b-button
                size="sm"
                :variant="
                  item.status === 'published'
                    ? 'outline-secondary'
                    : 'outline-success'
                "
                class="mr-1"
                :disabled="busyId === item._id"
                @click="togglePublish(item)"
              >
                <span
                  v-if="busyId === item._id && busyAction === 'publish'"
                  class="spinner-border spinner-border-sm mr-1"
                />
                {{
                  item.status === "published"
                    ? $t("releaseNotes.actions.unpublish")
                    : $t("releaseNotes.actions.publish")
                }}
              </b-button>
              <b-button
                size="sm"
                variant="outline-danger"
                @click="askDelete(item)"
              >
                {{ $t("releaseNotes.actions.delete") }}
              </b-button>
            </div>
          </template>
        </b-table>
      </b-card>
    </b-col>

    <!-- ===================== Editor ===================== -->
    <b-modal
      v-model="editorVisible"
      size="xl"
      scrollable
      no-close-on-backdrop
      :title="
        draft._id
          ? $t('releaseNotes.editor.editTitle', {
              version: draft.version || noteTitle(original),
            })
          : $t('releaseNotes.editor.createTitle')
      "
    >
      <b-row>
        <b-col lg="7">
          <p v-if="draft._id" class="small text-muted mb-2">
            {{
              $t("releaseNotes.help.currentStatus", {
                status: $t(
                  `releaseNotes.status.${noteStatus(original || draft)}`
                ),
              })
            }}
          </p>
          <b-row>
            <b-col md="4">
              <b-form-group
                :label="$t('releaseNotes.fields.version')"
                label-for="rn-version"
                :description="
                  isReleaseTag(draft.tag) ? '' : $t('releaseNotes.help.version')
                "
              >
                <b-form-input
                  id="rn-version"
                  v-model.trim="draft.version"
                  placeholder="v2.0.2"
                  size="sm"
                />
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group
                :label="$t('releaseNotes.fields.tag')"
                label-for="rn-tag"
              >
                <b-form-select
                  id="rn-tag"
                  v-model="draft.tag"
                  :options="tagOptions"
                  size="sm"
                />
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group
                :label="$t('releaseNotes.fields.sourceLocale')"
                label-for="rn-source"
              >
                <b-form-select
                  id="rn-source"
                  v-model="draft.sourceLocale"
                  :options="localeOptions"
                  size="sm"
                />
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group
                :label="$t('releaseNotes.fields.importance')"
                label-for="rn-importance"
                :description="$t('releaseNotes.help.importance')"
              >
                <b-form-select
                  id="rn-importance"
                  v-model="draft.importance"
                  :options="importanceOptions"
                  size="sm"
                />
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group
                :label="$t('releaseNotes.fields.audience')"
                label-for="rn-audience"
                :description="$t('releaseNotes.help.audience')"
              >
                <b-form-select
                  id="rn-audience"
                  v-model="draft.audience"
                  :options="audienceOptions"
                  size="sm"
                />
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group
                :label="$t('releaseNotes.fields.publishedAt')"
                label-for="rn-published-at"
                :description="$t('releaseNotes.help.publishedAt')"
              >
                <b-form-input
                  id="rn-published-at"
                  v-model="publishedAtLocal"
                  type="datetime-local"
                  size="sm"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <h6 class="font-weight-bold">
            {{ $t("releaseNotes.editor.lifecycle") }}
          </h6>
          <b-row>
            <b-col v-if="draft.tag === 'issue'" md="4">
              <b-form-group
                :label="$t('releaseNotes.fields.issueStatus')"
                label-for="rn-issue-status"
                :description="$t('releaseNotes.help.issueStatus')"
              >
                <b-form-select
                  id="rn-issue-status"
                  v-model="draft.issueStatus"
                  :options="issueStatusOptions"
                  size="sm"
                />
                <small
                  v-if="
                    original &&
                    original.resolvedAt &&
                    draft.issueStatus === 'resolved'
                  "
                  class="d-block text-success mt-1"
                >
                  {{
                    $t("releaseNotes.help.resolvedAt", {
                      date: formatDate(original.resolvedAt),
                    })
                  }}
                </small>
              </b-form-group>
            </b-col>
            <b-col v-if="draft.tag === 'upcoming'" md="4">
              <b-form-group
                :label="$t('releaseNotes.fields.expectedAt')"
                label-for="rn-expected-at"
                :description="$t('releaseNotes.help.expectedAt')"
              >
                <b-form-input
                  id="rn-expected-at"
                  v-model="expectedAtLocal"
                  type="date"
                  size="sm"
                />
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group
                :label="$t('releaseNotes.fields.expiresAt')"
                label-for="rn-expires-at"
                :description="$t('releaseNotes.help.expiresAt')"
              >
                <b-form-input
                  id="rn-expires-at"
                  v-model="expiresAtLocal"
                  type="datetime-local"
                  size="sm"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <div class="d-flex align-items-center mb-2">
            <h6 class="font-weight-bold mb-0 mr-auto">
              {{ $t("releaseNotes.editor.texts") }}
            </h6>
            <b-button
              size="sm"
              variant="outline-primary"
              :disabled="translating"
              @click="requestTranslate"
            >
              <span
                v-if="translating"
                class="spinner-border spinner-border-sm mr-1"
              />
              {{
                $t("releaseNotes.actions.translate", {
                  locale: (draft.sourceLocale || "").toUpperCase(),
                })
              }}
            </b-button>
          </div>
          <div v-if="translateError" class="alert alert-warning py-2 small">
            {{ translateError }}
          </div>
          <div v-if="translateInfo" class="alert alert-success py-2 small">
            {{ translateInfo }}
          </div>

          <b-tabs v-model="activeTab" small class="mb-3">
            <b-tab v-for="loc in locales" :key="loc">
              <template #title>
                <span
                  :class="{ 'font-weight-bold': loc === draft.sourceLocale }"
                >
                  {{ loc.toUpperCase() }}
                </span>
                <span
                  v-if="isLocaleEmpty(loc)"
                  class="rn-dot"
                  :title="$t('releaseNotes.help.emptyLocale')"
                />
              </template>
              <div class="pt-3">
                <p
                  v-if="isLocaleEmpty(loc) && loc !== draft.sourceLocale"
                  class="small text-muted"
                >
                  {{ $t("releaseNotes.help.emptyLocale") }}
                </p>
                <b-form-group :label-for="`rn-title-${loc}`">
                  <template #label>
                    {{ $t("releaseNotes.fields.title") }}
                    <span
                      class="small ml-1"
                      :class="
                        titleLength(loc) > 60 ? 'text-danger' : 'text-muted'
                      "
                    >
                      {{ titleLength(loc) }}/60
                    </span>
                  </template>
                  <b-form-input
                    :id="`rn-title-${loc}`"
                    v-model="draft.translations[loc].title"
                    size="sm"
                    :state="titleLength(loc) > 60 ? false : null"
                  />
                </b-form-group>
                <b-form-group
                  :label="$t('releaseNotes.fields.summary')"
                  :label-for="`rn-summary-${loc}`"
                  :description="$t('releaseNotes.help.summary')"
                >
                  <b-form-textarea
                    :id="`rn-summary-${loc}`"
                    v-model="draft.translations[loc].summary"
                    rows="2"
                    size="sm"
                  />
                  <small v-if="sentenceCount(loc) > 2" class="text-warning">
                    {{ $t("releaseNotes.help.summaryTooLong") }}
                  </small>
                </b-form-group>
                <b-form-group
                  :label="$t('releaseNotes.fields.ctaLabel')"
                  :label-for="`rn-cta-${loc}`"
                >
                  <b-form-input
                    :id="`rn-cta-${loc}`"
                    v-model="draft.translations[loc].ctaLabel"
                    size="sm"
                  />
                </b-form-group>
              </div>
            </b-tab>
          </b-tabs>

          <h6 class="font-weight-bold">
            {{ $t("releaseNotes.editor.shared") }}
          </h6>
          <b-form-group
            :label="$t('releaseNotes.fields.ctaPath')"
            label-for="rn-cta-path"
            :description="$t('releaseNotes.help.ctaPath')"
          >
            <b-form-select
              id="rn-cta-path"
              v-model="ctaSelect"
              :options="ctaOptions"
              size="sm"
            />
          </b-form-group>
          <b-form-group
            v-if="ctaCustomMode"
            :label="$t('releaseNotes.fields.ctaCustom')"
            label-for="rn-cta-custom"
            :description="$t('releaseNotes.help.ctaCustom')"
          >
            <b-form-input
              id="rn-cta-custom"
              v-model.trim="draft.ctaPath"
              placeholder="/projects"
              size="sm"
            />
          </b-form-group>
          <b-form-group
            :label="$t('releaseNotes.fields.imageUrl')"
            label-for="rn-image"
            :description="$t('releaseNotes.help.imageUrl')"
          >
            <b-form-input
              id="rn-image"
              v-model.trim="draft.imageUrl"
              placeholder="https://"
              size="sm"
            />
          </b-form-group>
          <b-form-group
            :label="$t('releaseNotes.fields.spotlight')"
            label-for="rn-spotlight"
            :description="$t('releaseNotes.help.spotlight')"
          >
            <b-form-select
              id="rn-spotlight"
              v-model="spotlightSelect"
              :options="spotlightOptions"
              size="sm"
            />
          </b-form-group>
          <b-form-group
            v-if="spotlightCustomMode"
            :label="$t('releaseNotes.fields.spotlightCustom')"
            label-for="rn-spotlight-custom"
            :description="$t('releaseNotes.help.spotlightCustom')"
          >
            <b-form-input
              id="rn-spotlight-custom"
              v-model.trim="draft.spotlight"
              placeholder="#btn-confronta"
              size="sm"
            />
            <small v-if="spotlightLooksLikeKey" class="d-block text-warning">
              {{
                $t("releaseNotes.help.spotlightLooksLikeKey", {
                  key: draft.spotlight,
                })
              }}
            </small>
          </b-form-group>
        </b-col>

        <!-- Live preview -->
        <b-col lg="5">
          <div class="rn-preview-sticky">
            <h6 class="font-weight-bold mb-1">
              {{
                $t("releaseNotes.preview.title", {
                  locale: activeLocale.toUpperCase(),
                })
              }}
            </h6>
            <p class="small text-muted">
              {{ $t("releaseNotes.preview.hint") }}
            </p>
            <template v-if="draft.importance === 'major'">
              <div
                v-if="showsBanner"
                class="rn-banner mb-1"
                :class="`rn-banner--${draft.tag}`"
              >
                <span class="rn-banner__icon">{{
                  draft.tag === "issue" ? "!" : "i"
                }}</span>
                <div class="rn-banner__text">
                  <span class="font-weight-bold">{{
                    previewText.title ||
                    $t("releaseNotes.preview.placeholderTitle")
                  }}</span>
                  <span v-if="previewText.summary" class="ml-1">{{
                    previewText.summary
                  }}</span>
                </div>
                <span
                  v-if="previewText.ctaLabel && draft.ctaPath"
                  class="rn-banner__cta"
                >
                  {{ previewText.ctaLabel }} &rarr;
                </span>
                <span
                  class="rn-banner__close"
                  :title="$t('releaseNotes.preview.dismiss')"
                  >&times;</span
                >
              </div>
              <p v-if="showsBanner" class="small text-muted mb-3">
                {{
                  $t("releaseNotes.preview.banner", {
                    until:
                      draft.tag === "issue"
                        ? $t("releaseNotes.preview.bannerUntilIssue")
                        : $t("releaseNotes.preview.bannerUntilExpiry"),
                  })
                }}
              </p>
              <p
                v-else-if="draft.tag === 'issue'"
                class="small text-muted mb-3"
              >
                {{ $t("releaseNotes.preview.noBannerResolved") }}
              </p>
            </template>
            <div class="rn-preview">
              <div class="d-flex align-items-center flex-wrap mb-2">
                <span class="rn-chip" :class="chipClass(draft)">
                  {{ $t(`releaseNotes.tag.${draft.tag || "new"}`) }}
                </span>
                <span
                  v-if="draft.tag === 'issue'"
                  class="rn-chip ml-1"
                  :class="`rn-chip--${issueStatusOf(draft)}`"
                >
                  {{ $t(`releaseNotes.issueStatus.${issueStatusOf(draft)}`) }}
                </span>
                <span
                  v-if="draft.tag === 'upcoming' && expectedAtIso"
                  class="small text-muted ml-2"
                >
                  {{
                    $t("releaseNotes.preview.expected", {
                      date: formatDay(expectedAtIso),
                    })
                  }}
                </span>
                <span class="small text-muted ml-2">{{ draft.version }}</span>
                <span
                  v-if="draft.audience === 'staff' || draft.audience === 'team'"
                  class="badge ml-auto"
                  :class="audienceBadge(draft.audience)"
                >
                  {{ $t(`releaseNotes.audience.${draft.audience}`) }}
                </span>
              </div>
              <img
                v-if="draft.imageUrl"
                :src="draft.imageUrl"
                class="rn-preview__img mb-2"
                alt=""
              />
              <div class="rn-preview__title">
                {{
                  previewText.title ||
                  $t("releaseNotes.preview.placeholderTitle")
                }}
              </div>
              <p v-if="previewText.summary" class="rn-preview__summary mb-2">
                {{ previewText.summary }}
              </p>
              <span
                v-if="previewText.ctaLabel && draft.ctaPath"
                class="rn-preview__cta"
              >
                {{ previewText.ctaLabel }} &rarr;
              </span>
            </div>
            <p v-if="draft.ctaPath" class="small text-muted mt-2 mb-1">
              {{
                $t("releaseNotes.preview.goesTo", {
                  target: destinationLabel(draft.ctaPath) || draft.ctaPath,
                })
              }}
            </p>
            <p
              v-if="draft.importance === 'major' && isHomeCardTag(draft.tag)"
              class="small text-muted mt-2 mb-1"
            >
              &#9733; {{ $t("releaseNotes.preview.homeCard") }}
            </p>
            <p v-if="draft.spotlight" class="small text-muted mb-1">
              <span class="rn-pulse" />
              {{
                $t("releaseNotes.preview.spotlight", {
                  selector: anchorLabel(draft.spotlight) || draft.spotlight,
                })
              }}
            </p>
            <p v-if="draftExpired" class="small text-danger mb-1">
              {{ $t("releaseNotes.preview.expired") }}
            </p>
          </div>
        </b-col>
      </b-row>

      <template #modal-footer>
        <div
          v-if="saveError"
          class="alert alert-danger py-1 px-2 mb-0 mr-auto small"
        >
          {{ saveError }}
        </div>
        <b-button
          variant="outline-secondary"
          :disabled="!!saving"
          @click="editorVisible = false"
        >
          {{ $t("releaseNotes.actions.cancel") }}
        </b-button>
        <b-button
          variant="outline-primary"
          :disabled="!!saving"
          @click="save('draft')"
        >
          <span
            v-if="saving === 'draft'"
            class="spinner-border spinner-border-sm mr-1"
          />
          {{
            original && original.status === "published"
              ? $t("releaseNotes.actions.unpublishSave")
              : $t("releaseNotes.actions.saveDraft")
          }}
        </b-button>
        <b-button
          variant="primary"
          :disabled="!!saving"
          @click="save('published')"
        >
          <span
            v-if="saving === 'published'"
            class="spinner-border spinner-border-sm mr-1"
          />
          {{ publishLabel }}
        </b-button>
      </template>
    </b-modal>

    <!-- Translate overwrite confirmation -->
    <b-modal
      v-model="translateConfirmVisible"
      :title="$t('releaseNotes.translate.confirmTitle')"
    >
      <p class="mb-0">
        {{
          $t("releaseNotes.translate.confirmText", {
            locales: filledTargetLocales.map((l) => l.toUpperCase()).join(", "),
          })
        }}
      </p>
      <template #modal-footer>
        <b-button
          variant="outline-secondary"
          @click="translateConfirmVisible = false"
        >
          {{ $t("releaseNotes.actions.cancel") }}
        </b-button>
        <b-button variant="outline-primary" @click="runTranslate(true)">
          {{ $t("releaseNotes.actions.fillEmpty") }}
        </b-button>
        <b-button variant="danger" @click="runTranslate(false)">
          {{ $t("releaseNotes.actions.overwriteAll") }}
        </b-button>
      </template>
    </b-modal>

    <!-- Delete confirmation -->
    <b-modal v-model="deleteVisible" :title="$t('releaseNotes.delete.title')">
      <p class="mb-0">
        {{
          $t("releaseNotes.delete.text", {
            title: deleteTarget
              ? noteTitle(deleteTarget) || deleteTarget.version
              : "",
          })
        }}
      </p>
      <div v-if="deleteError" class="alert alert-danger py-2 mt-3 mb-0">
        {{ deleteError }}
      </div>
      <template #modal-footer>
        <b-button
          variant="outline-secondary"
          :disabled="deleting"
          @click="deleteVisible = false"
        >
          {{ $t("releaseNotes.actions.cancel") }}
        </b-button>
        <b-button variant="danger" :disabled="deleting" @click="confirmDelete">
          <span v-if="deleting" class="spinner-border spinner-border-sm mr-1" />
          {{ $t("releaseNotes.actions.delete") }}
        </b-button>
      </template>
    </b-modal>

    <!-- Import from Jira -->
    <b-modal
      v-model="jiraVisible"
      size="lg"
      scrollable
      :title="$t('releaseNotes.jira.title')"
    >
      <b-row class="align-items-end">
        <b-col md="5">
          <b-form-group
            :label="$t('releaseNotes.jira.version')"
            label-for="rn-jira-version"
          >
            <b-form-input
              id="rn-jira-version"
              v-model.trim="jira.version"
              size="sm"
              @keyup.enter="loadJiraIssues"
            />
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group
            :label="$t('releaseNotes.jira.locale')"
            label-for="rn-jira-locale"
          >
            <b-form-select
              id="rn-jira-locale"
              v-model="jira.locale"
              :options="localeOptions"
              size="sm"
            />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group>
            <b-button
              size="sm"
              block
              :disabled="jira.loading || !jira.version"
              @click="loadJiraIssues"
            >
              <span
                v-if="jira.loading"
                class="spinner-border spinner-border-sm mr-1"
              />
              {{ $t("releaseNotes.actions.load") }}
            </b-button>
          </b-form-group>
        </b-col>
      </b-row>

      <div v-if="jira.error" class="alert alert-warning py-2">
        {{ jira.error }}
      </div>

      <template v-if="jira.issues">
        <div class="d-flex align-items-center mb-2">
          <h6 class="font-weight-bold mb-0 mr-auto">
            {{ $t("releaseNotes.jira.issues") }}
            <span class="small text-muted ml-1">
              {{
                $t("releaseNotes.jira.selected", {
                  count: jira.selected.length,
                })
              }}
            </span>
          </h6>
          <b-button
            size="sm"
            variant="link"
            class="p-0 mr-3"
            @click="jira.selected = jira.issues.map((i) => i.key)"
          >
            {{ $t("releaseNotes.actions.selectAll") }}
          </b-button>
          <b-button
            size="sm"
            variant="link"
            class="p-0"
            @click="jira.selected = []"
          >
            {{ $t("releaseNotes.actions.selectNone") }}
          </b-button>
        </div>
        <p v-if="!jira.issues.length" class="text-muted small">
          {{ $t("releaseNotes.jira.noIssues") }}
        </p>
        <div v-else class="rn-issue-list mb-3">
          <b-form-checkbox
            v-for="issue in jira.issues"
            :key="issue.key"
            v-model="jira.selected"
            :value="issue.key"
            class="rn-issue"
          >
            <span class="font-weight-bold mr-1">{{ issue.key }}</span>
            <span class="badge badge-light mr-1">{{ issue.type }}</span>
            <span class="badge badge-secondary mr-1">{{ issue.status }}</span>
            {{ issue.summary }}
          </b-form-checkbox>
        </div>
        <div class="text-right mb-3">
          <b-button
            size="sm"
            :disabled="jira.drafting || !jira.selected.length"
            @click="draftFromJira"
          >
            <span
              v-if="jira.drafting"
              class="spinner-border spinner-border-sm mr-1"
            />
            {{ $t("releaseNotes.actions.draft") }}
          </b-button>
        </div>
      </template>

      <div v-if="jira.draftError" class="alert alert-warning py-2">
        {{ jira.draftError }}
      </div>

      <template v-if="jira.drafts">
        <h6 class="font-weight-bold">{{ $t("releaseNotes.jira.drafts") }}</h6>
        <p v-if="!jira.drafts.length" class="text-muted small">
          {{ $t("releaseNotes.jira.noDrafts") }}
        </p>
        <div v-for="(d, i) in jira.drafts" :key="i" class="rn-draft mb-2">
          <div class="d-flex align-items-center mb-1">
            <span class="rn-chip" :class="`rn-chip--${d.tag}`">
              {{ $t(`releaseNotes.tag.${d.tag || "new"}`) }}
            </span>
            <span class="font-weight-bold ml-2">{{ d.title }}</span>
            <span class="small text-muted ml-2">{{
              (d.issueKeys || []).join(", ")
            }}</span>
          </div>
          <p class="small mb-1">{{ d.summary }}</p>
          <div class="d-flex align-items-center">
            <span v-if="d.error" class="small text-danger mr-auto">{{
              d.error
            }}</span>
            <b-button
              size="sm"
              variant="outline-primary"
              class="ml-auto"
              :disabled="d.creating || d.created"
              @click="createFromDraft(d)"
            >
              <span
                v-if="d.creating"
                class="spinner-border spinner-border-sm mr-1"
              />
              {{
                d.created
                  ? $t("releaseNotes.actions.created")
                  : $t("releaseNotes.actions.createDraft")
              }}
            </b-button>
          </div>
        </div>
      </template>

      <template #modal-footer>
        <b-button variant="outline-secondary" @click="jiraVisible = false">
          {{ $t("releaseNotes.actions.close") }}
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import services from "./../services";

const LOCALES = ["it", "en", "de", "es", "fr", "pt", "zh"];
const LOCALE_NAMES = {
  it: "Italiano",
  en: "English",
  de: "Deutsch",
  es: "Español",
  fr: "Français",
  pt: "Português",
  zh: "中文",
};
const TEXT_KEYS = ["title", "summary", "body", "ctaLabel"];
const EDITABLE_KEYS = [
  "version",
  "tag",
  "importance",
  "status",
  "audience",
  "publishedAt",
  "sourceLocale",
  "translations",
  "ctaPath",
  "imageUrl",
  "spotlight",
  "issueStatus",
  "expectedAt",
  "expiresAt",
];

// Tags grouped as in the editor select (see the Updates v2 contract)
const TAG_GROUPS = [
  { group: "release", tags: ["new", "improved", "fixed"] },
  { group: "status", tags: ["issue"] },
  { group: "announcements", tags: ["upcoming", "announcement"] },
];
const RELEASE_TAGS = TAG_GROUPS[0].tags;
// major + these tags = banner on every page; the rest = card on Home
const BANNER_TAGS = ["issue", "announcement"];
const CUSTOM = "__custom__";

// CTA destination catalog (`@` keys resolved by Foresight)
const PAGE_DESTINATIONS = {
  "@home": "home",
  "@projects": "projects",
  "@compare": "compare",
  "@new-analysis": "newAnalysis",
};
const SETTINGS_PANELS = [
  "profilo",
  "sicurezza",
  "notifiche",
  "preferenze",
  "team",
  "piano",
];
const PROJECT_TABS = [
  "overview",
  "metrics",
  "insights",
  "scenes",
  "aoi",
  "optimize",
];
const DESTINATION_KEYS = [
  ...Object.keys(PAGE_DESTINATIONS),
  ...SETTINGS_PANELS.map((p) => `@settings/${p}`),
];

// Spotlight anchor catalog (`data-whats-new` keys in Foresight), by page
const ANCHOR_GROUPS = [
  {
    group: "home",
    keys: ["home-new-analysis", "home-compare", "home-shared-tab"],
  },
  { group: "header", keys: ["header-notifications", "header-export"] },
  { group: "projects", keys: ["projects-new-analysis"] },
  {
    group: "project",
    keys: [
      ...PROJECT_TABS.map((t) => `project-tab-${t}`),
      "project-overlay-selector",
      "project-team-notes",
    ],
  },
  { group: "compare", keys: ["compare-add"] },
  { group: "settings", keys: ["settings-team"] },
];
const ANCHOR_KEYS = ANCHOR_GROUPS.reduce((all, g) => all.concat(g.keys), []);
const ANCHOR_KEY_RE = /^[a-z0-9-]+$/;
const camel = (key) => key.replace(/-([a-z0-9])/g, (_, c) => c.toUpperCase());

const emptyText = () => ({ title: "", summary: "", body: "", ctaLabel: "" });

const normalizeTranslations = (translations) => {
  const out = {};
  LOCALES.forEach((loc) => {
    const t = (translations && translations[loc]) || {};
    out[loc] = {};
    TEXT_KEYS.forEach((k) => {
      out[loc][k] = t[k] || "";
    });
  });
  return out;
};

const pad = (n) => String(n).padStart(2, "0");
const isoToLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};
const localInputToIso = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d.toISOString();
};
// Day-only fields (expectedAt) are stored at 12:00 UTC so the calendar day
// reads the same in every European/American timezone.
const isoToDateInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const dateInputToIso = (value) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || "")) return null;
  const d = new Date(`${value}T12:00:00.000Z`);
  return isNaN(d.getTime()) ? null : d.toISOString();
};

export default {
  data() {
    return {
      pageTitle: "page.releaseNotes.title",
      locales: LOCALES,
      notes: [],
      loading: false,
      listError: "",
      actionError: "",
      busyId: null,
      busyAction: "",

      // editor
      editorVisible: false,
      original: null,
      draft: this.blankNote(),
      publishedAtLocal: "",
      expectedAtLocal: "",
      expiresAtLocal: "",
      ctaCustomMode: false,
      spotlightCustomMode: false,
      activeTab: 0,
      saving: false,
      saveError: "",
      translating: false,
      translateError: "",
      translateInfo: "",
      translateConfirmVisible: false,

      // delete
      deleteVisible: false,
      deleteTarget: null,
      deleting: false,
      deleteError: "",

      // jira
      jiraVisible: false,
      jira: this.blankJira(),
    };
  },
  computed: {
    tableFields() {
      const f = (key) => ({
        key,
        label: this.$t(`releaseNotes.fields.${key}`),
      });
      return [
        f("version"),
        f("title"),
        f("tag"),
        f("importance"),
        f("audience"),
        f("status"),
        f("publishedAt"),
        { ...f("views"), class: "text-right" },
        { ...f("clicks"), class: "text-right" },
        { ...f("actions"), class: "text-right" },
      ];
    },
    tagOptions() {
      return TAG_GROUPS.map(({ group, tags }) => ({
        label: this.$t(`releaseNotes.tagGroup.${group}`),
        options: tags.map((value) => ({
          value,
          text: this.$t(`releaseNotes.tag.${value}`),
        })),
      }));
    },
    importanceOptions() {
      return ["minor", "major"].map((value) => ({
        value,
        text: this.$t(`releaseNotes.importance.${value}`),
      }));
    },
    audienceOptions() {
      return ["all", "team", "staff"].map((value) => ({
        value,
        text: this.$t(`releaseNotes.audience.${value}`),
      }));
    },
    issueStatusOptions() {
      return ["ongoing", "resolved"].map((value) => ({
        value,
        text: this.$t(`releaseNotes.issueStatus.${value}`),
      }));
    },
    ctaOptions() {
      const opt = (value) => ({ value, text: this.destinationLabel(value) });
      const group = (name, keys) => ({
        label: this.$t(`releaseNotes.dest.group.${name}`),
        options: keys.map(opt),
      });
      return [
        { value: "", text: this.$t("releaseNotes.choice.none") },
        group("pages", Object.keys(PAGE_DESTINATIONS)),
        group(
          "settings",
          SETTINGS_PANELS.map((p) => `@settings/${p}`)
        ),
        { value: CUSTOM, text: this.$t("releaseNotes.choice.customPath") },
      ];
    },
    spotlightOptions() {
      return [
        { value: "", text: this.$t("releaseNotes.choice.none") },
        ...ANCHOR_GROUPS.map(({ group, keys }) => ({
          label: this.$t(`releaseNotes.anchorGroup.${group}`),
          options: keys.map((value) => ({
            value,
            text: this.anchorLabel(value),
          })),
        })),
        {
          value: CUSTOM,
          text: this.$t("releaseNotes.choice.customSelector"),
        },
      ];
    },
    ctaSelect: {
      get() {
        return this.ctaCustomMode ? CUSTOM : this.draft.ctaPath || "";
      },
      set(value) {
        if (value === CUSTOM) {
          if (DESTINATION_KEYS.includes(this.draft.ctaPath))
            this.draft.ctaPath = "";
          this.ctaCustomMode = true;
        } else {
          this.ctaCustomMode = false;
          this.draft.ctaPath = value;
        }
      },
    },
    spotlightSelect: {
      get() {
        return this.spotlightCustomMode ? CUSTOM : this.draft.spotlight || "";
      },
      set(value) {
        if (value === CUSTOM) {
          if (ANCHOR_KEYS.includes(this.draft.spotlight))
            this.draft.spotlight = "";
          this.spotlightCustomMode = true;
        } else {
          this.spotlightCustomMode = false;
          this.draft.spotlight = value;
        }
      },
    },
    spotlightLooksLikeKey() {
      const value = this.draft.spotlight || "";
      return ANCHOR_KEY_RE.test(value) && !ANCHOR_KEYS.includes(value);
    },
    expectedAtIso() {
      return dateInputToIso(this.expectedAtLocal);
    },
    draftExpired() {
      const iso = localInputToIso(this.expiresAtLocal);
      return !!iso && new Date(iso) <= new Date();
    },
    showsBanner() {
      return (
        this.draft.importance === "major" &&
        BANNER_TAGS.includes(this.draft.tag) &&
        !(this.draft.tag === "issue" && this.draft.issueStatus === "resolved")
      );
    },
    localeOptions() {
      return LOCALES.map((value) => ({
        value,
        text: `${value.toUpperCase()} – ${LOCALE_NAMES[value]}`,
      }));
    },
    activeLocale() {
      return LOCALES[this.activeTab] || this.draft.sourceLocale || "en";
    },
    previewText() {
      const own = this.draft.translations[this.activeLocale];
      if (own && TEXT_KEYS.some((k) => own[k])) return own;
      return this.draft.translations[this.draft.sourceLocale] || emptyText();
    },
    filledTargetLocales() {
      return LOCALES.filter(
        (loc) => loc !== this.draft.sourceLocale && !this.isLocaleEmpty(loc)
      );
    },
    publishLabel() {
      const iso = localInputToIso(this.publishedAtLocal);
      if (iso && new Date(iso) > new Date())
        return this.$t("releaseNotes.actions.schedule");
      if (this.original && this.original.status === "published")
        return this.$t("releaseNotes.actions.update");
      return this.$t("releaseNotes.actions.publish");
    },
  },
  watch: {
    "draft.tag"(tag) {
      // the server defaults a known issue to ongoing; mirror it in the form
      if (tag === "issue" && !this.draft.issueStatus)
        this.draft.issueStatus = "ongoing";
    },
  },
  created() {
    this.loadNotes();
  },
  methods: {
    blankNote() {
      const locale = this.$i18n && this.$i18n.locale === "it" ? "it" : "en";
      return {
        _id: null,
        version: "",
        tag: "new",
        importance: "minor",
        status: "draft",
        audience: "all",
        publishedAt: null,
        sourceLocale: locale,
        translations: normalizeTranslations({}),
        ctaPath: "",
        imageUrl: "",
        spotlight: "",
        issueStatus: null,
        expectedAt: null,
        expiresAt: null,
      };
    },
    isReleaseTag(tag) {
      return RELEASE_TAGS.includes(tag || "new");
    },
    isHomeCardTag(tag) {
      return !BANNER_TAGS.includes(tag);
    },
    issueStatusOf(note) {
      return note && note.issueStatus === "resolved" ? "resolved" : "ongoing";
    },
    chipClass(note) {
      const tag = (note && note.tag) || "new";
      if (tag === "issue" && this.issueStatusOf(note) === "resolved")
        return "rn-chip--issue-resolved";
      return `rn-chip--${tag}`;
    },
    audienceBadge(audience) {
      if (audience === "staff") return "badge-warning";
      if (audience === "team") return "badge-info";
      return "badge-light";
    },
    destinationLabel(key) {
      if (!key || key[0] !== "@") return "";
      if (PAGE_DESTINATIONS[key])
        return this.$t(`releaseNotes.dest.${PAGE_DESTINATIONS[key]}`);
      const panel = key.startsWith("@settings/") && key.slice(10);
      if (panel && SETTINGS_PANELS.includes(panel))
        return this.$t("releaseNotes.dest.settings", {
          panel: this.$t(`releaseNotes.panel.${panel}`),
        });
      return "";
    },
    anchorLabel(key) {
      if (!ANCHOR_KEYS.includes(key)) return "";
      if (key.startsWith("project-tab-"))
        return this.$t("releaseNotes.anchor.projectTab", {
          tab: this.$t(`releaseNotes.projectTab.${key.slice(12)}`),
        });
      return this.$t(`releaseNotes.anchor.${camel(key)}`);
    },
    blankJira() {
      return {
        version: "v2.0.2",
        locale: this.$i18n && this.$i18n.locale === "it" ? "it" : "en",
        loading: false,
        error: "",
        issues: null,
        selected: [],
        drafting: false,
        draftError: "",
        drafts: null,
      };
    },
    errorMessage(error, fallbackKey) {
      const response = error && error.response;
      const body = response && response.body;
      const code = body && body.code;
      if (code === "jira_not_configured")
        return this.$t("releaseNotes.errors.jiraNotConfigured");
      if (code === "translation_unavailable")
        return this.$t("releaseNotes.errors.translationUnavailable");
      const status = (error && error.status) || (response && response.status);
      if (status === 401 || status === 403)
        return this.$t("releaseNotes.errors.unauthorized");
      const message =
        body && typeof body.message === "string" ? body.message : "";
      // A 404 without a specific API error means the route itself is missing
      if (status === 404 && (!message || /^not found$/i.test(message)))
        return this.$t("releaseNotes.errors.notFound");
      const fallback = this.$t(fallbackKey || "releaseNotes.errors.generic");
      return message ? `${fallback} (${message})` : fallback;
    },
    noteTitle(note) {
      const t = note && note.translations;
      if (!t) return "";
      const own = t[note.sourceLocale];
      if (own && own.title) return own.title;
      const any = LOCALES.map((l) => t[l]).find((x) => x && x.title);
      return any ? any.title : "";
    },
    noteStatus(note) {
      if (!note || note.status !== "published") return "draft";
      if (note.publishedAt && new Date(note.publishedAt) > new Date())
        return "scheduled";
      if (note.expiresAt && new Date(note.expiresAt) <= new Date())
        return "expired";
      return "published";
    },
    statusBadge(status) {
      return {
        draft: "badge-secondary",
        scheduled: "badge-info",
        published: "badge-success",
        expired: "badge-dark",
      }[status];
    },
    formatDate(iso) {
      if (!iso) return "—";
      const d = new Date(iso);
      if (isNaN(d.getTime())) return "—";
      return d.toLocaleString(this.$i18n.locale, {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    formatDay(iso) {
      if (!iso) return "—";
      const d = new Date(iso);
      if (isNaN(d.getTime())) return "—";
      return d.toLocaleDateString(this.$i18n.locale, {
        year: "numeric",
        month: "short",
        day: "2-digit",
      });
    },
    isLocaleEmpty(loc) {
      const t = this.draft.translations[loc];
      return !t || !TEXT_KEYS.some((k) => (t[k] || "").trim());
    },
    titleLength(loc) {
      return ((this.draft.translations[loc] || {}).title || "").length;
    },
    sentenceCount(loc) {
      const s = ((this.draft.translations[loc] || {}).summary || "").trim();
      if (!s) return 0;
      return s.split(/[.!?。！？]+(?:\s+|$)/).filter((x) => x.trim()).length;
    },
    payloadOf(note) {
      const payload = {};
      EDITABLE_KEYS.forEach((k) => {
        payload[k] = note[k];
      });
      payload.translations = normalizeTranslations(note.translations);
      ["ctaPath", "imageUrl", "spotlight", "expectedAt", "expiresAt"].forEach(
        (k) => {
          payload[k] = payload[k] ? payload[k] : null;
        }
      );
      // tag-specific fields only travel with their tag
      payload.issueStatus =
        payload.tag === "issue" ? this.issueStatusOf(note) : null;
      if (payload.tag !== "upcoming") payload.expectedAt = null;
      return payload;
    },

    // ---------- list ----------
    loadNotes() {
      this.loading = true;
      this.listError = "";
      return services.ReleaseNoteService.retrieveReleaseNotes()
        .then((notes) => {
          this.notes = Array.isArray(notes) ? notes : [];
        })
        .catch((error) => {
          this.listError = this.errorMessage(error, "releaseNotes.errors.load");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    togglePublish(note) {
      const payload = this.payloadOf(note);
      if (note.status === "published") {
        payload.status = "draft";
      } else {
        payload.status = "published";
        const keepSchedule =
          note.publishedAt && new Date(note.publishedAt) > new Date();
        payload.publishedAt = keepSchedule
          ? note.publishedAt
          : new Date().toISOString();
      }
      this.updateRow(note, payload, "publish");
    },
    markResolved(note) {
      const payload = this.payloadOf(note);
      payload.issueStatus = "resolved";
      this.updateRow(note, payload, "resolve");
    },
    updateRow(note, payload, action) {
      this.busyId = note._id;
      this.busyAction = action;
      this.actionError = "";
      services.ReleaseNoteService.updateReleaseNote(note._id, payload)
        .then((updated) =>
          this.replaceNote(
            updated && updated._id ? updated : { ...note, ...payload }
          )
        )
        .catch((error) => {
          this.actionError = this.errorMessage(
            error,
            "releaseNotes.errors.save"
          );
        })
        .finally(() => {
          this.busyId = null;
          this.busyAction = "";
        });
    },
    replaceNote(updated) {
      const idx = this.notes.findIndex((n) => n._id === updated._id);
      if (idx >= 0) this.notes.splice(idx, 1, updated);
      else this.notes.unshift(updated);
    },
    askDelete(note) {
      this.deleteTarget = note;
      this.deleteError = "";
      this.deleteVisible = true;
    },
    confirmDelete() {
      const note = this.deleteTarget;
      if (!note) return;
      this.deleting = true;
      this.deleteError = "";
      services.ReleaseNoteService.deleteReleaseNote(note._id)
        .then(() => {
          this.notes = this.notes.filter((n) => n._id !== note._id);
          this.deleteVisible = false;
          this.deleteTarget = null;
        })
        .catch((error) => {
          this.deleteError = this.errorMessage(
            error,
            "releaseNotes.errors.delete"
          );
        })
        .finally(() => {
          this.deleting = false;
        });
    },

    // ---------- editor ----------
    openEditor(note) {
      this.original = note ? JSON.parse(JSON.stringify(note)) : null;
      const base = note
        ? { ...this.blankNote(), ...JSON.parse(JSON.stringify(note)) }
        : this.blankNote();
      base.translations = normalizeTranslations(base.translations);
      if (!LOCALES.includes(base.sourceLocale)) base.sourceLocale = "en";
      this.draft = base;
      this.publishedAtLocal = isoToLocalInput(base.publishedAt);
      this.expectedAtLocal = isoToDateInput(base.expectedAt);
      this.expiresAtLocal = isoToLocalInput(base.expiresAt);
      this.ctaCustomMode =
        !!base.ctaPath && !DESTINATION_KEYS.includes(base.ctaPath);
      this.spotlightCustomMode =
        !!base.spotlight && !ANCHOR_KEYS.includes(base.spotlight);
      this.activeTab = Math.max(0, LOCALES.indexOf(base.sourceLocale));
      this.saveError = "";
      this.translateError = "";
      this.translateInfo = "";
      this.editorVisible = true;
    },
    validate() {
      if (!this.draft.version && this.isReleaseTag(this.draft.tag))
        return this.$t("releaseNotes.errors.versionRequired");
      if (this.ctaCustomMode && !this.draft.ctaPath)
        return this.$t("releaseNotes.errors.ctaCustomRequired");
      if (this.spotlightCustomMode && !this.draft.spotlight)
        return this.$t("releaseNotes.errors.spotlightCustomRequired");
      if (
        !(this.draft.translations[this.draft.sourceLocale].title || "").trim()
      )
        return this.$t("releaseNotes.errors.titleRequired");
      const tooLong = LOCALES.find((loc) => this.titleLength(loc) > 60);
      if (tooLong)
        return this.$t("releaseNotes.errors.titleTooLong", {
          locale: tooLong.toUpperCase(),
        });
      return "";
    },
    save(status) {
      const invalid = this.validate();
      if (invalid) {
        this.saveError = invalid;
        return;
      }
      const payload = this.payloadOf(this.draft);
      payload.status = status;
      payload.publishedAt = localInputToIso(this.publishedAtLocal);
      payload.expiresAt = localInputToIso(this.expiresAtLocal);
      payload.expectedAt =
        payload.tag === "upcoming"
          ? dateInputToIso(this.expectedAtLocal)
          : null;
      if (status === "published" && !payload.publishedAt) {
        payload.publishedAt = new Date().toISOString();
      }
      this.saving = status;
      this.saveError = "";
      const request = this.draft._id
        ? services.ReleaseNoteService.updateReleaseNote(this.draft._id, payload)
        : services.ReleaseNoteService.createReleaseNote(payload);
      request
        .then((saved) => {
          this.editorVisible = false;
          if (saved && saved._id) this.replaceNote(saved);
          else this.loadNotes();
        })
        .catch((error) => {
          this.saveError = this.errorMessage(error, "releaseNotes.errors.save");
        })
        .finally(() => {
          this.saving = false;
        });
    },
    requestTranslate() {
      this.translateError = "";
      this.translateInfo = "";
      const source = this.draft.translations[this.draft.sourceLocale];
      if (!(source.title || "").trim()) {
        this.translateError = this.$t("releaseNotes.translate.sourceEmpty");
        return;
      }
      if (this.filledTargetLocales.length) this.translateConfirmVisible = true;
      else this.runTranslate(false);
    },
    runTranslate(onlyEmpty) {
      this.translateConfirmVisible = false;
      const sourceLocale = this.draft.sourceLocale;
      const targets = LOCALES.filter(
        (loc) => loc !== sourceLocale && (!onlyEmpty || this.isLocaleEmpty(loc))
      );
      if (!targets.length) return;
      const payload = {
        sourceLocale,
        source: { ...this.draft.translations[sourceLocale] },
      };
      if (onlyEmpty) payload.targetLocales = targets;
      this.translating = true;
      services.ReleaseNoteService.translateReleaseNote(payload)
        .then((result) => {
          const translations = (result && result.translations) || {};
          targets.forEach((loc) => {
            if (!translations[loc]) return;
            const t = emptyText();
            TEXT_KEYS.forEach((k) => {
              t[k] = translations[loc][k] || "";
            });
            this.$set(this.draft.translations, loc, t);
          });
          this.translateInfo = this.$t("releaseNotes.translate.done");
        })
        .catch((error) => {
          this.translateError = this.errorMessage(
            error,
            "releaseNotes.errors.generic"
          );
        })
        .finally(() => {
          this.translating = false;
        });
    },

    // ---------- jira ----------
    openJira() {
      const keepVersion = this.jira.version;
      this.jira = { ...this.blankJira(), version: keepVersion || "v2.0.2" };
      this.jiraVisible = true;
    },
    loadJiraIssues() {
      if (!this.jira.version || this.jira.loading) return;
      this.jira.loading = true;
      this.jira.error = "";
      this.jira.issues = null;
      this.jira.selected = [];
      this.jira.drafts = null;
      this.jira.draftError = "";
      services.ReleaseNoteService.retrieveReleaseNoteJiraIssues(
        this.jira.version
      )
        .then((result) => {
          const issues = (result && result.issues) || [];
          this.jira.issues = issues;
          this.jira.selected = issues.map((i) => i.key);
        })
        .catch((error) => {
          this.jira.error = this.errorMessage(
            error,
            "releaseNotes.errors.generic"
          );
        })
        .finally(() => {
          this.jira.loading = false;
        });
    },
    draftFromJira() {
      const issues = (this.jira.issues || [])
        .filter((i) => this.jira.selected.includes(i.key))
        .map(({ key, summary, description, type }) => ({
          key,
          summary: summary || "",
          description: description || "",
          type: type || "",
        }));
      if (!issues.length) return;
      this.jira.drafting = true;
      this.jira.draftError = "";
      this.jira.drafts = null;
      services.ReleaseNoteService.draftReleaseNotes({
        locale: this.jira.locale,
        version: this.jira.version,
        issues,
      })
        .then((result) => {
          this.jira.drafts = ((result && result.drafts) || []).map((d) => ({
            ...d,
            creating: false,
            created: false,
            error: "",
          }));
        })
        .catch((error) => {
          this.jira.draftError = this.errorMessage(
            error,
            "releaseNotes.errors.generic"
          );
        })
        .finally(() => {
          this.jira.drafting = false;
        });
    },
    createFromDraft(d) {
      const locale = this.jira.locale;
      const translations = normalizeTranslations({});
      translations[locale] = {
        title: (d.title || "").slice(0, 60),
        summary: d.summary || "",
        body: d.body || "",
        ctaLabel: "",
      };
      const payload = this.payloadOf({
        ...this.blankNote(),
        version: this.jira.version,
        tag: ["new", "improved", "fixed"].includes(d.tag) ? d.tag : "new",
        sourceLocale: locale,
        translations,
      });
      d.creating = true;
      d.error = "";
      services.ReleaseNoteService.createReleaseNote(payload)
        .then((saved) => {
          d.created = true;
          if (saved && saved._id) this.replaceNote(saved);
          else this.loadNotes();
        })
        .catch((error) => {
          d.error = this.errorMessage(error, "releaseNotes.errors.save");
        })
        .finally(() => {
          d.creating = false;
        });
    },
  },
};
</script>

<style scoped>
.rn-chip {
  display: inline-block;
  padding: 1px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  background: #ede9fe;
  color: #5b2dd6;
}
.rn-chip--improved {
  background: #dbeafe;
  color: #1d4ed8;
}
.rn-chip--fixed,
.rn-chip--issue-resolved,
.rn-chip--resolved {
  background: #dcfce7;
  color: #15803d;
}
.rn-chip--issue {
  background: #fef3c7;
  color: #b45309;
}
.rn-chip--ongoing {
  background: #fffbeb;
  color: #b45309;
  box-shadow: inset 0 0 0 1px #f59e0b;
}
.rn-chip--upcoming {
  background: #ccfbf1;
  color: #0f766e;
}
.rn-chip--announcement {
  background: #e2e8f0;
  color: #334155;
}
.rn-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 12px;
  border: 1px solid #fcd34d;
  background: #fffbeb;
  color: #92400e;
}
.rn-banner--announcement {
  border-color: #cbd5e1;
  background: #f1f5f9;
  color: #334155;
}
.rn-banner__icon {
  flex: 0 0 20px;
  height: 20px;
  border-radius: 50%;
  background: #f59e0b;
  color: #fff;
  text-align: center;
  line-height: 20px;
  font-weight: 700;
}
.rn-banner--announcement .rn-banner__icon {
  background: #64748b;
}
.rn-banner__text {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.rn-banner__cta {
  font-weight: 600;
  white-space: nowrap;
  text-decoration: underline;
}
.rn-banner__close {
  font-size: 16px;
  line-height: 1;
  opacity: 0.6;
}
.rn-dot {
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #f59e0b;
  margin-left: 4px;
  vertical-align: middle;
}
.rn-preview-sticky {
  position: sticky;
  top: 0;
}
.rn-preview {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px;
  background: #fff;
  box-shadow: 0 6px 20px rgba(22, 49, 80, 0.08);
}
.rn-preview__img {
  width: 100%;
  aspect-ratio: 16 / 9;
  object-fit: cover;
  border-radius: 8px;
  background: #f3f4f6;
}
.rn-preview__title {
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 4px;
  word-break: break-word;
}
.rn-preview__summary {
  font-size: 13px;
  color: #374151;
}
.rn-preview__body {
  font-size: 12px;
  color: #6b7280;
}
.rn-preview__cta {
  display: inline-block;
  margin-top: 4px;
  padding: 6px 12px;
  border-radius: 8px;
  background: #5b2dd6;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
}
.rn-pulse {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #5b2dd6;
  margin-right: 4px;
  box-shadow: 0 0 0 0 rgba(91, 45, 214, 0.6);
  animation: rn-pulse 1.6s infinite;
}
@keyframes rn-pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(91, 45, 214, 0.6);
  }
  70% {
    box-shadow: 0 0 0 8px rgba(91, 45, 214, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(91, 45, 214, 0);
  }
}
.rn-issue-list {
  max-height: 320px;
  overflow-y: auto;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 8px 12px;
}
.rn-issue {
  padding: 4px 0;
  font-size: 13px;
}
.rn-draft {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 10px 12px;
}
.rn-pre {
  white-space: pre-line;
}
</style>
